// src/services/apiService.ts - Fixed version based on your original

import { SelectedLocation } from '../types';

// Simple API configuration - similar to your original
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Your existing types (keeping them exactly the same)
export interface QuickSignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  username: string;
  sex: string;
  phone?: string;
}

export interface OnboardingStepData {
  step: number;
  data: {
    // Step 1: Housing
    location?: SelectedLocation[];
    housingSituation?: string;
    budget?: string;
    moveInDate?: string;
    age?: number;

    // Step 2: Lifestyle (flat structure)
    occupation?: string;
    languages?: string[];
    cleanliness?: string;
    socialLevel?: string;
    workSchedule?: string;
    hasPets?: string[];
    okWithPets?: string[];
    bio?: string;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: any[];
}

// Enhanced API Service Class - Based on your original but more flexible
class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
    
    // Simple logging for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 API Service initialized:', {
        baseURL: this.baseURL,
        environment: process.env.REACT_APP_ENVIRONMENT || 'default'
      });
    }
  }

  // Your original request method with small improvements
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}/${endpoint.startsWith('/') ? endpoint.slice(1) : endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    console.log('🌐 Making request to:', url);

    try {
      const response = await fetch(url, defaultOptions);
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new Error('Invalid JSON response from server');
      }

      if (!response.ok) {
        const error: any = new Error(data.message || `HTTP error! status: ${response.status}`);
        error.response = { data };
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ API Request Error:', error);
      
      // Better error message for connection issues
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Cannot connect to backend server. Please check if the server is running.');
      }
      
      throw error;
    }
  }

  // Your existing auth methods (keeping them exactly the same)
  async quickSignup(data: QuickSignupData): Promise<ApiResponse<{ user: any; token: string }>> {
    const response = await this.request<{ user: any; token: string }>('auth/quick-signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.success) {
      throw new Error(response.message);
    }

    return response;
  }

  async loginUser(credentials: { usernameOrEmail: string; password: string }): Promise<ApiResponse<{ user: any; token: string }>> {
    // Call backend
    const raw = await this.request<any>('auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    // Normalize to ApiResponse shape expected by useAuth
    // Backend returns { success, token, user } while frontend expects { success, data: { token, user } }
    if (raw && typeof raw === 'object' && (raw as any).token && (raw as any).user) {
      return {
        success: true,
        message: 'Login successful',
        data: {
          token: (raw as any).token as string,
          user: (raw as any).user,
        },
      };
    }

    // If backend already returns ApiResponse shape, pass through
    if (raw && raw.success !== undefined) {
      return raw as ApiResponse<{ user: any; token: string }>;
    }

    throw new Error('Unexpected login response format');
  }

  // Your existing onboarding methods (keeping the logic exactly the same)
  async updateOnboardingStep(data: OnboardingStepData, token: string): Promise<ApiResponse<any>> {
    let endpoint = '';
    let payload: any = {};

    switch (data.step) {
      case 1:
        endpoint = 'auth/onboarding/housing';
        
        const selectedLocations = data.data.location?.map(loc => ({
          borough: loc.borough,
          neighborhood: loc.neighborhood,
          id: loc.id || `${loc.neighborhood.toLowerCase().replace(/\s+/g, '-')}-${loc.borough.toLowerCase()}`
        })) || [];

        // moveInDate is stored as an enum string — pass it through as-is, no conversion
        const moveInDate = data.data.moveInDate;

        // Parse budget into rentMin/rentMax. "3000+" gets a sentinel upper bound of 9999.
        let rentMin = 1000;
        let rentMax = 2000;
        const budget = data.data.budget;
        if (budget) {
          if (budget === '3000+') {
            rentMin = 3000;
            rentMax = 9999;
          } else {
            const parts = budget.split('-');
            rentMin = parseInt(parts[0].replace(/\D/g, '') || '1000');
            rentMax = parseInt(parts[1]?.replace(/\D/g, '') || parts[0].replace(/\D/g, '') || '2000');
          }
        }

        payload = {
          selectedLocations,
          housingSituation: data.data.housingSituation,
          rentMin,
          rentMax,
          moveInDate: moveInDate,
          age: data.data.age
        };
        break;

      case 2:
        endpoint = 'auth/onboarding/lifestyle';
        payload = {
          occupation: data.data.occupation,
          languages: data.data.languages || [],
          cleanliness: data.data.cleanliness,
          socialLevel: data.data.socialLevel,
          workSchedule: data.data.workSchedule,
          hasPets: data.data.hasPets || [],
          okWithPets: data.data.okWithPets || [],
          bio: data.data.bio || ''
        };
        break;

      case 3:
        endpoint = 'auth/onboarding/complete';
        payload = {};
        break;

      default:
        throw new Error(`Invalid onboarding step: ${data.step}`);
    }

    const response = await this.request<any>(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(response.message);
    }

    return response;
  }

  async completeOnboarding(token: string): Promise<ApiResponse<any>> {
    const response = await this.request<any>('auth/onboarding/complete', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({}),
    });

    if (!response.success) {
      throw new Error(response.message);
    }

    return response;
  }

  // Your existing preview methods
  async getPreviewProfiles(): Promise<ApiResponse<{ profiles: any[] }>> {
    const response = await this.request<{ profiles: any[] }>('preview/profiles');
    
    if (!response.success) {
      throw new Error(response.message);
    }

    return response;
  }

  async getPreviewStats(): Promise<ApiResponse<{ stats: any }>> {
    const response = await this.request<{ stats: any }>('preview/stats');
    
    if (!response.success) {
      throw new Error(response.message);
    }

    return response;
  }

  // User profile methods
  async getUserProfile(userId: string, token?: string): Promise<ApiResponse<any>> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await this.request<any>(`users/${userId}`, {
      method: 'GET',
      headers,
    });

    if (!response.success) {
      throw new Error(response.message);
    }

    return response;
  }

  async updateUserProfile(userId: string, updates: any, token: string): Promise<ApiResponse<any>> {
    const response = await this.request<any>(`users/${userId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates),
    });

    if (!response.success) {
      throw new Error(response.message);
    }

    return response;
  }

  async uploadProfileImage(
    userId: string,
    imageFile: File,
    token: string
  ): Promise<{ success: boolean; imageUrl: string; message?: string }> {
    const formData = new FormData();
    formData.append('image', imageFile);

    const url = `${this.baseURL}/users/${userId}/image`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type - browser will set it with boundary for multipart/form-data
      },
      body: formData,
    });

    if (!response.ok) {
      let errorMessage = 'Failed to upload image';
      try {
        const error = await response.json();
        errorMessage = error.message || errorMessage;
      } catch {
        // If response is not JSON, use default message
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Generic POST helper — used by authAPI.forgotPassword and authAPI.resetPassword
  async post<T = any>(endpoint: string, body: object): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Simple utility methods
  async healthCheck() {
    try {
      const healthUrl = this.baseURL.replace('/api', '/health');
      const response = await fetch(healthUrl);
      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  // Get current configuration for debugging
  getConfig() {
    return {
      baseURL: this.baseURL,
      environment: process.env.NODE_ENV,
      customApiUrl: process.env.REACT_APP_API_URL,
      backendType: process.env.REACT_APP_BACKEND_TYPE
    };
  }
}

// Create singleton instance
const apiService = new ApiService();

// Your existing exports (keeping them exactly the same)
export const authAPI = {
  quickSignup: (data: QuickSignupData) => apiService.quickSignup(data),
  login: (usernameOrEmail: string, password: string) => apiService.loginUser({ usernameOrEmail, password }),
  forgotPassword: (email: string) => apiService.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) => apiService.post('/auth/reset-password', { token, newPassword }),
};

export const previewAPI = {
  getProfiles: () => apiService.getPreviewProfiles(),
  getStats: () => apiService.getPreviewStats(),
};

export const onboardingAPI = {
  updateStep: (data: OnboardingStepData, token: string) => apiService.updateOnboardingStep(data, token),
  completeOnboarding: (token: string) => apiService.completeOnboarding(token),
};

export const userAPI = {
  getProfile: (userId: string, token?: string) => apiService.getUserProfile(userId, token),
  updateProfile: (userId: string, updates: any, token: string) => apiService.updateUserProfile(userId, updates, token),
  uploadProfileImage: (userId: string, imageFile: File, token: string) => apiService.uploadProfileImage(userId, imageFile, token),
};

// Export utilities
export const getApiConfig = () => apiService.getConfig();
export const healthCheck = () => apiService.healthCheck();

export default apiService;